// NEXORA product page — public product names: NEXORA MINI™, NEXORA ATLAS™
// Internal source references (do NOT surface publicly in headings/copy):
//   NEXORA MINI  ← manufacturer source: TP-01 (Standard / EDU) intelligent humanoid
//   NEXORA ATLAS ← manufacturer source: KONKA / "Gongga" mobile humanoid platform (ruidiculous.com/robot/3)
// All specifications below are verified against the source documents supplied
// by Neil AI leadership. Unverified fields render "Contact Neil AI for
// configuration details" rather than guessing.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowRight,
  PlayCircle,
  Cpu,
  Bot,
  Sparkles,
  Palette,
  Layers,
  ShieldCheck,
  Radio,
  BatteryCharging,
  Users,
  GraduationCap,
  Building2,
  Church,
  Hotel,
  Store,
  Wrench,
  Monitor,
  Mic,
  Camera,
  Zap,
  Download,
  Phone,
  CheckCircle2,
  Info,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

// ---------- Product imagery (CDN assets) ----------
import miniHeroAsset from "@/assets/nexora/nexora-mini-hero.jpg.asset.json";
import miniLineupAsset from "@/assets/nexora/nexora-mini-lineup.jpg.asset.json";
import miniYellowAsset from "@/assets/nexora/nexora-mini-yellow.jpg.asset.json";
import miniFloatingAsset from "@/assets/nexora/nexora-mini-floating.jpg.asset.json";
import miniTealAsset from "@/assets/nexora/nexora-mini-teal.jpg.asset.json";
import miniSilverAsset from "@/assets/nexora/nexora-mini-silver.jpg.asset.json";
import miniHomeAsset from "@/assets/nexora/nexora-mini-home.jpg.asset.json";
import miniBrandSheetAsset from "@/assets/nexora/nexora-mini-brand-sheet.jpeg.asset.json";
import atlasKonkaAsset from "@/assets/nexora/nexora-atlas-spec.jpeg.asset.json";
import atlasHomeCareAsset from "@/assets/nexora/nexora-atlas-home-caregiving.jpeg.asset.json";
import atlasElderlyRdAsset from "@/assets/nexora/nexora-atlas-elderly-rd.jpeg.asset.json";
import nexoraHomeLogoAsset from "@/assets/nexora/nexora-home-logo.png.asset.json";
import atlasCaregivingAsset from "@/assets/nexora/nexora-atlas-caregiving.jpeg.asset.json";
import atlasElderlyCareAsset from "@/assets/nexora/nexora-atlas-elderly-care.jpeg.asset.json";
import atlasHomeServicesAsset from "@/assets/nexora/nexora-atlas-home-services.jpeg.asset.json";
import atlasRDAsset from "@/assets/nexora/nexora-atlas-rd.jpeg.asset.json";

const ATLAS_CAREGIVING = atlasCaregivingAsset.url;
const ATLAS_ELDERLY_CARE = atlasElderlyCareAsset.url;
const ATLAS_HOME_SERVICES = atlasHomeServicesAsset.url;
const ATLAS_RD = atlasRDAsset.url;

const MINI_BRAND_SHEET = miniBrandSheetAsset.url;
const ATLAS_KONKA = atlasKonkaAsset.url;
const ATLAS_HOME_CARE = atlasHomeCareAsset.url;
const ATLAS_ELDERLY_RD = atlasElderlyRdAsset.url;
const NEXORA_HOME_LOGO = nexoraHomeLogoAsset.url;

const MINI_HERO = miniHeroAsset.url;
const MINI_LINEUP = miniLineupAsset.url;
const MINI_YELLOW = miniYellowAsset.url;
const MINI_FLOATING = miniFloatingAsset.url;
const MINI_TEAL = miniTealAsset.url;
const MINI_SILVER = miniSilverAsset.url;
const MINI_HOME = miniHomeAsset.url;

// Map color name → best-matching product photo (fallback to hero shot)
const COLOR_IMAGE: Record<string, string> = {
  Yellow: MINI_YELLOW,
  Cyan: MINI_TEAL,
  Grey: MINI_SILVER,
  White: MINI_LINEUP,
  Black: MINI_LINEUP,
  Green: MINI_LINEUP,
  Pink: MINI_HERO,
  Red: MINI_HERO,
};

// ---------- Color data (public colors for NEXORA MINI) ----------
const MINI_COLORS: {
  name: string;
  swatch: string; // css color for the swatch pill
  note?: string;
}[] = [
  { name: "Black", swatch: "#1a1a1a" },
  { name: "Cyan", swatch: "#22d3ee" },
  { name: "Green", swatch: "#4ade80" },
  { name: "Grey", swatch: "#9ca3af" },
  { name: "Pink", swatch: "#f472b6" },
  { name: "Red", swatch: "#ef4444" },
  { name: "White", swatch: "#f1f5f9" },
  { name: "Yellow", swatch: "#facc15" },
];

// ---------- MINI specs (verified) ----------
const MINI_SPECS: {
  label: string;
  standard: string;
  edu: string;
}[] = [
  { label: "Product category", standard: "Intelligent humanoid robot", edu: "Intelligent humanoid robot" },
  { label: "Height", standard: "850 mm (~33.5 in)", edu: "850 mm (~33.5 in)" },
  { label: "Weight (battery included)", standard: "34 kg (~75 lb)", edu: "34 kg (~75 lb)" },
  { label: "Head degrees of freedom", standard: "6", edu: "6" },
  { label: "Body degrees of freedom", standard: "16", edu: "16" },
  { label: "Battery life", standard: "Up to 3 hours", edu: "Up to 3 hours" },
  { label: "Forward speed", standard: "Up to 0.6 m/s", edu: "Up to 0.6 m/s" },
  { label: "Screen", standard: "19-inch display", edu: "19-inch display" },
  { label: "Communication", standard: "4G, Bluetooth, Wi-Fi", edu: "4G, Bluetooth, Wi-Fi" },
  { label: "Quick-swap battery", standard: "Yes", edu: "Yes" },
  { label: "Speaker", standard: "Yes", edu: "Yes" },
  { label: "Microphone array", standard: "Yes", edu: "Yes" },
  { label: "Manual controller", standard: "Yes", edu: "Yes" },
  { label: "App & management platform", standard: "Yes", edu: "Not listed for this configuration" },
  { label: "Group control", standard: "Yes", edu: "Not listed for this configuration" },
  { label: "Intelligent following & formation parade", standard: "Yes", edu: "Not listed for this configuration" },
  { label: "Voice interaction", standard: "Yes", edu: "Yes" },
  { label: "Accessory expansion port", standard: "Yes", edu: "Yes" },
  { label: "Secondary development", standard: "Not listed for this configuration", edu: "Yes" },
];

// ---------- ATLAS specs (verified from source page) ----------
const ATLAS_SPEC_GROUPS: { group: string; rows: { label: string; value: string }[] }[] = [
  {
    group: "Physical",
    rows: [
      { label: "Height", value: "1,660 mm (~65 in)" },
      { label: "Weight", value: "Under 35 kg (~77 lb)" },
      { label: "Whole-body degrees of freedom", value: "13" },
      { label: "Screen", value: "8-inch head touchscreen" },
    ],
  },
  {
    group: "AI Computing",
    rows: [
      { label: "Computing platform", value: "NVIDIA Jetson AGX Orin" },
      { label: "Perception / decision stack", value: "Advanced 3DSG-class scene understanding for cross-space, cross-scenario tasks" },
    ],
  },
  {
    group: "Mobility",
    rows: [
      { label: "Mobile base", value: "Lightweight mobile humanoid chassis" },
      { label: "Navigation", value: "LiDAR + depth-camera assisted" },
      { label: "Maximum speed", value: "Contact Neil AI for configuration details" },
    ],
  },
  {
    group: "Robotic Arm",
    rows: [
      { label: "End-effector payload", value: "Up to 3 kg" },
      { label: "End effector", value: "Quick-swap / replaceable" },
      { label: "Arm reach & max working height", value: "Contact Neil AI for configuration details" },
    ],
  },
  {
    group: "Sensors",
    rows: [
      { label: "Head depth camera", value: "Yes" },
      { label: "Wrist depth camera", value: "Yes" },
      { label: "LiDAR", value: "Yes" },
      { label: "Anti-drop / safety sensing", value: "Yes" },
    ],
  },
  {
    group: "Audio & Interaction",
    rows: [
      { label: "Speaker", value: "Yes" },
      { label: "Microphone array", value: "Yes" },
      { label: "Voice interaction", value: "Yes" },
      { label: "Head touchscreen UI", value: "Yes (8-inch)" },
    ],
  },
  {
    group: "Connectivity",
    rows: [
      { label: "Wireless", value: "Wi-Fi, Bluetooth" },
      { label: "USB expansion ports", value: "Yes (multiple locations)" },
      { label: "High-power external DC output", value: "Yes" },
      { label: "Remote controller", value: "Supported" },
    ],
  },
  {
    group: "Power & Charging",
    rows: [
      { label: "Battery runtime", value: "Approximately 6 hours" },
      { label: "Autonomous charging", value: "Supported" },
      { label: "Battery capacity / swap time", value: "Contact Neil AI for configuration details" },
    ],
  },
  {
    group: "Software & Development",
    rows: [
      { label: "SDK & sample programs", value: "Yes — developer documentation, SDK, and example routines" },
      { label: "Secondary development", value: "Supported" },
      { label: "Developer community", value: "Available" },
    ],
  },
];

// ---------- Applications ----------
const APPLICATIONS: { icon: React.ElementType; label: string }[] = [
  { icon: Users, label: "Homes & Families" },
  { icon: GraduationCap, label: "Schools & Universities" },
  { icon: Bot, label: "STEM & Robotics Labs" },
  { icon: Hotel, label: "Hotels & Hospitality" },
  { icon: Store, label: "Retail & Restaurants" },
  { icon: Building2, label: "Museums & Innovation Centers" },
  { icon: Church, label: "Churches & Events" },
  { icon: Sparkles, label: "Trade Shows & Activations" },
  { icon: Wrench, label: "Research & Developer Programs" },
  { icon: ShieldCheck, label: "Corporate Reception" },
  { icon: Layers, label: "Senior-Engagement Programs" },
  { icon: Cpu, label: "Healthcare Innovation Pilots" },
];

// ---------- FAQ ----------
const FAQ: { q: string; a: string }[] = [
  { q: "What is NEXORA?", a: "NEXORA is Neil AI Global Solutions' intelligent robotics family — two purpose-built platforms designed for engaging experiences across homes, learning, business, hospitality, events, and research." },
  { q: "What is NEXORA MINI™?", a: "NEXORA MINI is a compact 850 mm intelligent humanoid with a 19-inch display, six-degree-of-freedom neck, voice interaction, quick-swap battery, and coordinated group performance capability. It ships in Standard and EDU configurations." },
  { q: "What is NEXORA ATLAS™?", a: "NEXORA ATLAS is an advanced mobile humanoid platform powered by NVIDIA Jetson AGX Orin, featuring a robotic arm, LiDAR, head + wrist depth cameras, 8-inch touchscreen, autonomous charging, and a full developer SDK." },
  { q: "What is the difference between the two robots?", a: "MINI focuses on expressive interaction, multimedia content delivery, and coordinated performances at a compact size. ATLAS is a full mobile humanoid built for AI research, computer vision, robotic-arm applications, and pilot deployments." },
  { q: "What colors are available for NEXORA MINI?", a: "Eight official colors: Black, Cyan, Green, Grey, Pink, Red, White, and Yellow. Availability may vary by configuration and production schedule." },
  { q: "How large is NEXORA MINI?", a: "850 mm tall (~33.5 in) and 34 kg (~75 lb) with battery included." },
  { q: "How long does the NEXORA MINI battery last?", a: "Up to 3 hours of runtime per battery. The quick-swap battery design allows rapid changeover between demonstrations or events." },
  { q: "What is the difference between Standard and EDU?", a: "Standard adds the App & Management Platform, group control, and intelligent following / formation parade. EDU adds Secondary Development support, making it ideal for approved robotics education, labs, and research." },
  { q: "Can NEXORA MINI display custom content?", a: "Yes. The 19-inch display and content-delivery platform can present branded messaging, educational media, event content, menus, and interactive visuals." },
  { q: "Can multiple NEXORA MINI robots operate together?", a: "Yes — the Standard configuration supports group control and coordinated performances including formation parades." },
  { q: "What processor powers NEXORA ATLAS?", a: "NVIDIA Jetson AGX Orin — a high-performance edge-AI computing platform for robotics and vision workloads." },
  { q: "What is NVIDIA Jetson AGX Orin?", a: "It is NVIDIA's flagship edge-AI computing module designed for on-device inference, sensor fusion, computer vision, and low-latency robotics processing." },
  { q: "Does NEXORA ATLAS include a robotic arm?", a: "Yes. It supports payloads up to 3 kg and features quick-swap, interchangeable end effectors." },
  { q: "Can NEXORA ATLAS navigate independently?", a: "It supports mapping and localization using LiDAR and depth cameras, and can return to charge autonomously. Final autonomy behavior depends on configuration and environment." },
  { q: "Can the robots be customized?", a: "Yes. Modular panels, developer SDKs, and expansion ports allow branded and application-specific customization." },
  { q: "Are developer tools available?", a: "Yes. ATLAS ships with full SDK, documentation, sample programs, and access to a developer community. MINI EDU adds secondary development." },
  { q: "Can the products be used by schools?", a: "Yes. MINI EDU is designed for classrooms, labs, and robotics programs. ATLAS supports advanced research and higher-education programs." },
  { q: "Can the products be used by businesses?", a: "Yes — hospitality, retail, events, corporate reception, museums, and pilot automation deployments are all supported." },
  { q: "Does Neil AI provide deployment and training?", a: "Yes. Neil AI Global Solutions provides configuration, deployment, staff training, and ongoing support." },
  { q: "How do I request pricing?", a: "Complete the Request a Demonstration form on this page or call (336) 203-3184." },
  { q: "How do I schedule a demonstration?", a: "Use the demonstration form below or contact Neil AI directly to schedule." },
  { q: "Where are NEXORA products available?", a: "NEXORA products are available through Neil AI Global Solutions. Contact Neil AI for current availability in your region." },
  { q: "Are all demonstrated functions included by default?", a: "No. Available capabilities depend on the installed hardware configuration, software version, sensors, and integrations. Neil AI will confirm supported capabilities during your assessment." },
  { q: "Are NEXORA robots medical devices?", a: "No. NEXORA robots are not medical devices and are not substitutes for licensed medical professionals, caregivers, or emergency services." },
];

// ---------- Nav sections ----------
const SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "nexora-mini", label: "NEXORA MINI" },
  { id: "colors", label: "Colors" },
  { id: "nexora-atlas", label: "NEXORA ATLAS" },
  { id: "specifications", label: "Specifications" },
  { id: "videos", label: "Videos" },
  { id: "applications", label: "Applications" },
  { id: "request-demo", label: "Request a Demo" },
];

// ---------- Form validation ----------
const demoSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(60),
  lastName: z.string().trim().min(1, "Last name is required").max(60),
  organization: z.string().trim().max(120).optional().or(z.literal("")),
  jobTitle: z.string().trim().max(120).optional().or(z.literal("")),
  email: z.string().trim().email("Valid email required").max(120),
  phone: z.string().trim().min(7, "Phone required").max(30),
  city: z.string().trim().max(80).optional().or(z.literal("")),
  state: z.string().trim().max(80).optional().or(z.literal("")),
  country: z.string().trim().max(80).optional().or(z.literal("")),
  industry: z.string().trim().max(80).optional().or(z.literal("")),
  productInterest: z.string().min(1, "Select a product interest"),
  intendedUse: z.string().min(1, "Select an intended use"),
  quantity: z.string().trim().max(20).optional().or(z.literal("")),
  timeline: z.string().trim().max(80).optional().or(z.literal("")),
  budget: z.string().trim().max(80).optional().or(z.literal("")),
  message: z.string().trim().max(1500).optional().or(z.literal("")),
  emailConsent: z.boolean(),
  smsConsent: z.boolean(),
});

// ---------- Section nav (sticky) ----------
const SectionNav = () => {
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div className="sticky top-24 md:top-28 z-40 -mx-4 px-4 py-2 backdrop-blur-xl bg-[#05070A]/85 border-y border-white/10">
      <nav
        aria-label="NEXORA sections"
        className="max-w-7xl mx-auto flex gap-1 overflow-x-auto no-scrollbar"
      >
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => scrollTo(s.id)}
            className="shrink-0 px-3 h-9 rounded-full text-xs font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
          >
            {s.label}
          </button>
        ))}
      </nav>
    </div>
  );
};

// ---------- Media placeholder ----------
const MediaSlot = ({ label, aspect = "aspect-video" }: { label: string; aspect?: string }) => (
  <div
    className={`relative ${aspect} w-full rounded-2xl border border-dashed border-white/15 bg-gradient-to-br from-white/[0.04] to-white/[0.01] flex items-center justify-center overflow-hidden`}
    role="img"
    aria-label={`${label} placeholder`}
  >
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(0,163,255,0.08),transparent_60%),radial-gradient(circle_at_70%_80%,rgba(214,168,79,0.06),transparent_60%)]" />
    <div className="relative text-center px-6">
      <PlayCircle className="w-10 h-10 text-white/40 mx-auto mb-2" />
      <p className="text-xs uppercase tracking-widest text-white/50">{label}</p>
      <p className="text-[11px] text-white/30 mt-1">Media uploads to CMS</p>
    </div>
  </div>
);

// ---------- Feature card ----------
const FeatureCard = ({ icon: Icon, title, children }: { icon: React.ElementType; title: string; children: React.ReactNode }) => (
  <Card className="bg-white/[0.03] border-white/10 p-5 rounded-2xl hover:border-primary/40 transition-colors">
    <Icon className="w-6 h-6 text-primary mb-3" aria-hidden />
    <h3 className="font-semibold text-white text-base mb-1.5">{title}</h3>
    <p className="text-sm text-slate-300 leading-relaxed">{children}</p>
  </Card>
);

// ---------- Page ----------
const Nexora = () => {
  const [activeColor, setActiveColor] = useState(MINI_COLORS[0].name);
  const activeSwatch = useMemo(
    () => MINI_COLORS.find((c) => c.name === activeColor)?.swatch ?? "#1a1a1a",
    [activeColor]
  );

  const [videoFilter, setVideoFilter] = useState<"all" | "mini" | "atlas">("all");

  const [form, setForm] = useState({
    firstName: "", lastName: "", organization: "", jobTitle: "",
    email: "", phone: "", city: "", state: "", country: "",
    industry: "", productInterest: "", intendedUse: "",
    quantity: "", timeline: "", budget: "", message: "",
    emailConsent: false, smsConsent: false,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setField = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = demoSchema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message || "Please check the form");
      return;
    }
    setLoading(true);
    const d = parsed.data;
    const { error } = await supabase.from("demo_requests").insert({
      name: `${d.firstName} ${d.lastName}`.trim(),
      email: d.email,
      phone: d.phone,
      business_name: d.organization || null,
      industry: d.industry || "NEXORA",
      interested_in: [d.productInterest, d.intendedUse].filter(Boolean),
      message: [
        `Source: /nexora`,
        d.jobTitle && `Title: ${d.jobTitle}`,
        (d.city || d.state || d.country) && `Location: ${[d.city, d.state, d.country].filter(Boolean).join(", ")}`,
        d.quantity && `Quantity: ${d.quantity}`,
        d.timeline && `Timeline: ${d.timeline}`,
        d.budget && `Budget: ${d.budget}`,
        `Email consent: ${d.emailConsent ? "yes" : "no"} · SMS consent: ${d.smsConsent ? "yes" : "no"}`,
        d.message && `\n${d.message}`,
      ].filter(Boolean).join("\n"),
    });
    setLoading(false);
    if (error) {
      toast.error("Could not submit. Please call (336) 203-3184.");
      return;
    }
    setDone(true);
    toast.success("Request received — a NEXORA specialist will reach out shortly.");
  };

  const videoCards = [
    { product: "mini", title: "NEXORA MINI — Home & Family Scenarios", desc: "Concept scenarios showing expressive interaction and multimedia engagement in home environments." },
    { product: "mini", title: "NEXORA MINI — Interactive Demonstration", desc: "Voice interaction, animated display expressions, and microphone-array response." },
    { product: "mini", title: "NEXORA MINI — Coordinated Performance", desc: "Group control and formation-parade demonstration (Standard configuration)." },
    { product: "mini", title: "NEXORA MINI — Content Display", desc: "19-inch display presenting branded and educational content." },
    { product: "atlas", title: "NEXORA ATLAS — Mobile Navigation", desc: "LiDAR + depth-camera assisted movement across environments." },
    { product: "atlas", title: "NEXORA ATLAS — Robotic-Arm Demonstration", desc: "Quick-swap end-effector performing object interaction (controlled test)." },
    { product: "atlas", title: "NEXORA ATLAS — AI Interaction", desc: "Edge-AI processing on NVIDIA Jetson AGX Orin — perception + voice." },
    { product: "atlas", title: "NEXORA ATLAS — Developer Demonstration", desc: "SDK example programs and secondary-development workflows." },
  ];
  const filteredVideos = videoCards.filter((v) => videoFilter === "all" || v.product === videoFilter);

  return (
    <>
      <SEO
        title="NEXORA Intelligent Robots | Neil AI Global Solutions"
        description="Explore NEXORA MINI and NEXORA ATLAS intelligent robots from Neil AI Global Solutions. Review features, colors, technical specifications, videos, applications, and demonstration options."
        canonical="https://www.neilai.ai/nexora"
        keywords="NEXORA robot, NEXORA MINI, NEXORA ATLAS, intelligent humanoid robot, AI companion robot, educational humanoid robot, business humanoid robot, mobile humanoid robot, NVIDIA Jetson AGX Orin robot, humanoid robot North Carolina, Neil AI robotics"
      />

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "Product",
              name: "NEXORA MINI",
              brand: { "@type": "Brand", name: "NEXORA" },
              manufacturer: { "@type": "Organization", name: "Neil AI Global Solutions LLC" },
              category: "Intelligent Humanoid Robot",
              description: "Compact 850 mm intelligent humanoid with 19-inch display, 6-DOF neck, voice interaction, quick-swap battery, and coordinated group performance. Standard and EDU configurations.",
              url: "https://www.neilai.ai/nexora#nexora-mini",
            },
            {
              "@context": "https://schema.org",
              "@type": "Product",
              name: "NEXORA ATLAS",
              brand: { "@type": "Brand", name: "NEXORA" },
              manufacturer: { "@type": "Organization", name: "Neil AI Global Solutions LLC" },
              category: "Mobile Humanoid Robotics Platform",
              description: "Advanced mobile humanoid platform powered by NVIDIA Jetson AGX Orin with robotic arm, LiDAR, depth cameras, 8-inch touchscreen, autonomous charging, and full developer SDK.",
              url: "https://www.neilai.ai/nexora#nexora-atlas",
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://www.neilai.ai/" },
                { "@type": "ListItem", position: 2, name: "Robotics", item: "https://www.neilai.ai/robots" },
                { "@type": "ListItem", position: 3, name: "NEXORA", item: "https://www.neilai.ai/nexora" },
              ],
            },
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: FAQ.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ]),
        }}
      />

      <div className="min-h-screen bg-[#05070A] text-white">
        <Navigation />

        {/* HERO */}
        <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
          <div aria-hidden className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(0,163,255,0.18),transparent_55%),radial-gradient(circle_at_80%_90%,rgba(214,168,79,0.12),transparent_55%)]" />
          <div className="relative max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <img
                src={NEXORA_HOME_LOGO}
                alt="Neil AI Nexora Home — Automate. Elevate. Dominate."
                className="mb-6 w-40 md:w-48 h-auto rounded-2xl border border-white/10 shadow-[0_10px_40px_-10px_rgba(0,163,255,0.5)]"
              />
              <Badge className="mb-5 bg-primary/15 text-primary border border-primary/30">
                Neil AI Global Solutions Presents
              </Badge>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
                Meet{" "}
                <span className="bg-gradient-to-r from-[#00A3FF] via-[#66D0FF] to-[#D6A84F] bg-clip-text text-transparent">
                  NEXORA™
                </span>
              </h1>
              <p className="mt-5 text-xl md:text-2xl text-white/90 font-light">
                Intelligent Robotics for Life, Learning, and Business.
              </p>
              <p className="mt-5 text-base md:text-lg text-slate-300 max-w-xl leading-relaxed">
                Discover two intelligent robotic platforms designed to create engaging experiences across homes, education, customer service, events, hospitality, research, business innovation, and emerging automation environments.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  size="lg"
                  className="h-14 px-6 rounded-full bg-gradient-to-r from-[#00A3FF] to-[#0057FF] hover:opacity-95 text-white font-semibold shadow-[0_10px_40px_-10px_rgba(0,163,255,0.6)]"
                  onClick={() => document.getElementById("request-demo")?.scrollIntoView({ behavior: "smooth" })}
                >
                  Request a Demonstration <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-6 rounded-full border-white/25 bg-white/5 hover:bg-white/10 text-white"
                  onClick={() => document.getElementById("overview")?.scrollIntoView({ behavior: "smooth" })}
                >
                  Explore the Robots
                </Button>
              </div>
              <button
                onClick={() => document.getElementById("videos")?.scrollIntoView({ behavior: "smooth" })}
                className="mt-5 inline-flex items-center gap-2 text-sm text-primary hover:text-white transition-colors"
              >
                <PlayCircle className="w-4 h-4" /> Watch NEXORA in Action
              </button>
            </div>

            <div className="relative">
              <div className="relative aspect-square rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#00A3FF]/10 via-transparent to-[#D6A84F]/10 shadow-[0_40px_120px_-30px_rgba(0,163,255,0.5)]">
                <img
                  src={MINI_HERO}
                  alt="NEXORA MINI intelligent humanoid robot"
                  className="absolute inset-0 w-full h-full object-contain p-4"
                  loading="eager"
                  fetchPriority="high"
                />
                <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 text-[10px] uppercase tracking-widest text-white/80">
                  NEXORA MINI™
                </div>
              </div>
              <div className="mt-4 grid min-w-0 grid-cols-3 gap-2 sm:gap-3">
                <div className="min-w-0 aspect-square overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-1"><img src={MINI_YELLOW} alt="NEXORA MINI in Yellow" className="h-full w-full object-contain" loading="lazy" /></div>
                <div className="min-w-0 aspect-square overflow-hidden rounded-2xl border border-white/10 bg-[#0d1a24] p-1"><img src={MINI_TEAL} alt="NEXORA MINI in Cyan" className="h-full w-full object-contain" loading="lazy" /></div>
                <div className="min-w-0 aspect-square overflow-hidden rounded-2xl border border-white/10 bg-black p-1"><img src={MINI_SILVER} alt="NEXORA MINI in Grey" className="h-full w-full object-contain" loading="lazy" /></div>
              </div>
            </div>
          </div>
        </section>

        <SectionNav />

        {/* OVERVIEW */}
        <section id="overview" className="scroll-mt-32 py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <p className="text-xs uppercase tracking-[0.25em] text-primary mb-3">The NEXORA Family</p>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
                Two Intelligent Robots.
                <span className="block bg-gradient-to-r from-[#00A3FF] to-[#D6A84F] bg-clip-text text-transparent">
                  One Connected Future.
                </span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* MINI card */}
              <Card className="bg-gradient-to-br from-white/[0.04] to-white/[0.01] border-white/10 p-6 md:p-8 rounded-3xl">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-[#f5e6b8] to-[#e8d4a0]">
                  <img src={MINI_HOME} alt="NEXORA MINI in a home environment with a child" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="mt-6">
                  <h3 className="text-2xl md:text-3xl font-bold">NEXORA MINI™</h3>
                  <p className="text-primary text-sm mt-1">The Interactive AI Companion for Life and Learning.</p>
                  <p className="mt-3 text-slate-300 text-sm leading-relaxed">
                    A compact intelligent humanoid designed for expressive interaction, multimedia content, education, events, customer engagement, coordinated performances, and flexible branded experiences.
                  </p>
                  <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-slate-300">
                    {["19-inch display","6-DOF expressive neck","Voice interaction","Quick-swap battery","Group control","Coordinated movement","8 official colors","Standard + EDU"].map((h) => (
                      <li key={h} className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary" /> {h}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="mt-6 rounded-full"
                    onClick={() => document.getElementById("nexora-mini")?.scrollIntoView({ behavior: "smooth" })}
                  >
                    Explore NEXORA MINI <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </Card>

              {/* ATLAS card */}
              <Card className="bg-gradient-to-br from-white/[0.04] to-white/[0.01] border-white/10 p-6 md:p-8 rounded-3xl">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-black">
                  <img src={ATLAS_HOME_CARE} alt="NEXORA ATLAS mobile humanoid in home and caregiving environments" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="mt-6">
                  <h3 className="text-2xl md:text-3xl font-bold">NEXORA ATLAS™</h3>
                  <p className="text-primary text-sm mt-1">Advanced Mobile Intelligence for Real-World Innovation.</p>
                  <p className="mt-3 text-slate-300 text-sm leading-relaxed">
                    A mobile humanoid robotics platform designed for advanced AI processing, autonomous movement, computer vision, voice interaction, robotic-arm applications, education, research, demonstrations, and custom business pilots.
                  </p>
                  <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-slate-300">
                    {["NVIDIA Jetson AGX Orin","Mobile humanoid base","Robotic arm (3 kg)","LiDAR + depth cameras","8\" head touchscreen","Autonomous charging","Voice interaction","Developer SDK"].map((h) => (
                      <li key={h} className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary" /> {h}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="mt-6 rounded-full"
                    onClick={() => document.getElementById("nexora-atlas")?.scrollIntoView({ behavior: "smooth" })}
                  >
                    Explore NEXORA ATLAS <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </Card>
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={() => document.getElementById("specifications")?.scrollIntoView({ behavior: "smooth" })}
                className="text-sm text-primary hover:text-white underline underline-offset-4"
              >
                Compare Models →
              </button>
            </div>
          </div>
        </section>

        {/* NEXORA MINI */}
        <section id="nexora-mini" className="scroll-mt-32 py-16 md:py-24 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-4">
            <div className="max-w-3xl mb-10">
              <p className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Compact Intelligent Humanoid</p>
              <h2 className="text-3xl md:text-5xl font-bold">NEXORA MINI™</h2>
              <p className="mt-3 text-xl text-white/90 font-light">Big Personality. Intelligent Interaction. Endless Possibilities.</p>
              <p className="mt-4 text-slate-300">
                NEXORA MINI is a compact intelligent humanoid created for engaging communication, expressive movement, multimedia presentations, education, entertainment, customer interaction, coordinated performances, and branded experiences.
              </p>
            </div>

            <a
              href={MINI_BRAND_SHEET}
              target="_blank"
              rel="noopener noreferrer"
              className="block mb-10 rounded-3xl overflow-hidden border border-white/10 bg-black shadow-[0_20px_80px_-30px_rgba(214,168,79,0.4)] hover:opacity-95 transition"
              aria-label="Open full NEXORA MINI brand & specification sheet"
            >
              <img src={MINI_BRAND_SHEET} alt="NEXORA MINI (TP-01) — Neil AI brand and full specification sheet" className="w-full h-auto" loading="lazy" />
            </a>

            <div className="hidden">{/* spacer marker */}</div>

            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <div className="relative aspect-square rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#faedc4] via-[#f2d98a] to-[#e8c46a]">
                <img src={MINI_FLOATING} alt="NEXORA MINI floating on a display platform" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <FeatureCard icon={Bot} title="Expressive Six-DOF Neck">A six-degree-of-freedom neck enables flexible head movement, expressive interaction, and more natural visual engagement.</FeatureCard>
                <FeatureCard icon={Monitor} title="19-Inch Content Display">The large integrated display supports multimedia presentations, branded messaging, educational content, event media, visitor information, menus, promotions, and interactive visual communication.</FeatureCard>
                <FeatureCard icon={Users} title="Group Control & Coordinated Performance">The Standard configuration supports group control, coordinated movement, formation parades, and synchronized performance applications.</FeatureCard>
                <FeatureCard icon={Sparkles} title="Flexible Content Delivery">The display and management platform can deliver rich visual content directly to audiences in customer-facing, educational, entertainment, and promotional environments.</FeatureCard>
                <FeatureCard icon={Mic} title="Multimodal Interaction">Voice interaction, a microphone array, animated display expressions, and supported perception features create a more engaging human–robot experience.</FeatureCard>
                <FeatureCard icon={Palette} title="Modular Visual Customization">Replaceable themed panels support creative visual customization, branded campaigns, special events, and approved intellectual-property experiences.</FeatureCard>
                <FeatureCard icon={Layers} title="Integrated Management Ecosystem">The Standard configuration combines fleet management, intelligent scheduling, group coordination, and content-delivery tools within one operational environment.</FeatureCard>
                <FeatureCard icon={BatteryCharging} title="Quick-Swap Battery">The quick-swap battery design helps organizations replace batteries efficiently and reduce interruptions during scheduled demonstrations or events.</FeatureCard>
                <FeatureCard icon={GraduationCap} title="Education & Development Configuration">The EDU configuration supports secondary development for approved robotics education, programming, demonstrations, laboratories, and research environments.</FeatureCard>
              </div>
            </div>

            {/* MINI spec table */}
            <div className="mt-14">
              <h3 className="text-2xl font-bold mb-4">NEXORA MINI Specifications</h3>
              <div className="overflow-x-auto rounded-2xl border border-white/10">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/90">Specification</TableHead>
                      <TableHead className="text-white/90">NEXORA MINI Standard</TableHead>
                      <TableHead className="text-white/90">NEXORA MINI EDU</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {MINI_SPECS.map((s) => (
                      <TableRow key={s.label} className="border-white/5">
                        <TableCell className="font-medium text-white">{s.label}</TableCell>
                        <TableCell className="text-slate-300">{s.standard}</TableCell>
                        <TableCell className="text-slate-300">{s.edu}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </section>

        {/* COLORS */}
        <section id="colors" className="scroll-mt-32 py-16 md:py-24 border-t border-white/5 bg-white/[0.015]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Color Configurator</p>
              <h2 className="text-3xl md:text-5xl font-bold">Choose Your NEXORA MINI Color</h2>
              <p className="mt-3 text-slate-300 text-sm">
                Eight official colors. Color availability may vary by configuration, production schedule, and order quantity — contact Neil AI for current availability.
              </p>
            </div>

            <div className="grid min-w-0 lg:grid-cols-[minmax(0,1fr)_320px] gap-8 items-center">
              <div
                className="relative min-w-0 aspect-square rounded-3xl border border-white/10 overflow-hidden transition-colors duration-500"
                style={{
                  background: `radial-gradient(circle at 50% 45%, ${activeSwatch}30, transparent 65%), linear-gradient(180deg, rgba(255,255,255,0.03), rgba(0,0,0,0.4))`,
                }}
                aria-live="polite"
              >
                <div className="absolute inset-x-0 top-0 bottom-20 flex items-center justify-center p-4 sm:p-6">
                  <img
                    key={activeColor}
                    src={COLOR_IMAGE[activeColor] ?? MINI_HERO}
                    alt={`NEXORA MINI in ${activeColor}`}
                    className="max-w-full max-h-full w-auto h-auto object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] animate-fade-in"
                    loading="lazy"
                  />
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex flex-col items-start gap-1 sm:bottom-5 sm:left-5 sm:right-5 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-white/60">Now viewing</p>
                    <p className="text-base font-semibold sm:text-xl">NEXORA MINI in {activeColor}</p>
                  </div>
                  <span className="text-[10px] text-white/40 sm:text-right">Reference photo — final finish may vary</span>
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-widest text-white/50 mb-3">Select a color</p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-1">
                  {MINI_COLORS.map((c) => {
                    const active = c.name === activeColor;
                    return (
                      <button
                        key={c.name}
                        type="button"
                        onClick={() => setActiveColor(c.name)}
                        aria-pressed={active}
                        aria-label={`NEXORA MINI in ${c.name}`}
                        className={`min-w-0 flex items-center gap-3 px-3 h-11 rounded-full border transition-all ${
                          active
                            ? "border-primary bg-primary/10 text-white"
                            : "border-white/10 text-slate-300 hover:border-white/25 hover:bg-white/5"
                        }`}
                      >
                        <span
                          className="w-6 h-6 rounded-full border border-white/20"
                          style={{ background: c.swatch }}
                          aria-hidden
                        />
                        <span className="text-sm font-medium">{c.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* NEXORA ATLAS */}
        <section id="nexora-atlas" className="scroll-mt-32 py-16 md:py-24 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-4">
            <div className="max-w-3xl mb-10">
              <p className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Advanced Mobile Humanoid Platform</p>
              <h2 className="text-3xl md:text-5xl font-bold">NEXORA ATLAS™</h2>
              <p className="mt-3 text-xl text-white/90 font-light">Intelligence That Moves, Sees, Interacts, and Adapts.</p>
              <p className="mt-4 text-slate-300">
                NEXORA ATLAS is an advanced mobile humanoid robotics platform designed for AI-powered interaction, computer vision, autonomous navigation, robotic-arm demonstrations, education, research, customer engagement, and customized automation pilots.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-white/10 bg-black">
                <img src={ATLAS_KONKA} alt="NEXORA ATLAS mobile humanoid platform with robotic arm" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <FeatureCard icon={Cpu} title="Edge AI Computing">NVIDIA Jetson AGX Orin delivers on-device inference for perception, navigation, and multimodal robotics workloads.</FeatureCard>
                <FeatureCard icon={Camera} title="Computer Vision">Head + wrist depth cameras support visual perception and object-recognition applications configured for your environment.</FeatureCard>
                <FeatureCard icon={Radio} title="LiDAR + Depth Sensing">Environmental sensing provides mapping, obstacle avoidance, and situational awareness during autonomous movement.</FeatureCard>
                <FeatureCard icon={Wrench} title="Robotic Arm">Quick-swap end effector with up to 3 kg payload — configurable for a wide range of demonstrations and pilots.</FeatureCard>
                <FeatureCard icon={Mic} title="Voice Interaction">Microphone array + speaker + supported conversational experiences.</FeatureCard>
                <FeatureCard icon={Monitor} title="8-Inch Head Touchscreen">Customer-facing controls, information display, demonstrations, and application interfaces.</FeatureCard>
                <FeatureCard icon={BatteryCharging} title="Autonomous Charging">Return-to-charge behavior supported. Runtime approximately 6 hours per charge.</FeatureCard>
                <FeatureCard icon={Zap} title="Expansion & Power">Multiple USB expansion ports and high-power external DC outputs for accessories and integrations.</FeatureCard>
                <FeatureCard icon={Layers} title="Developer Environment">Full SDK, documentation, example programs, and access to an active developer community.</FeatureCard>
              </div>
            </div>

            {/* Jetson feature */}
            <div className="mt-14 relative overflow-hidden rounded-3xl border border-white/10 p-6 md:p-10 bg-gradient-to-br from-[#00A3FF]/10 via-transparent to-[#0057FF]/10">
              <div className="grid md:grid-cols-[1fr_1fr] gap-8 items-center">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Advanced Edge AI Computing</p>
                  <h3 className="text-2xl md:text-4xl font-bold">Powered by NVIDIA Jetson AGX Orin</h3>
                  <p className="mt-4 text-slate-300 leading-relaxed">
                    At the center of NEXORA ATLAS is the NVIDIA Jetson AGX Orin platform, providing high-performance edge AI computing for advanced robotics applications — edge inference, sensor fusion, computer-vision workloads, navigation intelligence, and low-latency local processing.
                  </p>
                  <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-slate-200">
                    {["Edge AI processing","Sensor fusion","Computer vision","AI inference","Navigation intelligence","Voice & multimodal","Robotics research","Developer experimentation"].map((x) => (
                      <li key={x} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" />{x}</li>
                    ))}
                  </ul>
                  <p className="mt-5 text-xs text-white/50 flex items-start gap-2">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    Available AI performance and supported applications depend on the installed hardware configuration, software, trained models, sensors, integration, and operating environment.
                  </p>
                </div>
                <div className="aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black">
                  <img src={ATLAS_ELDERLY_RD} alt="NEXORA ATLAS deployed for elderly care and R&D" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SPECIFICATIONS + COMPARISON */}
        <section id="specifications" className="scroll-mt-32 py-16 md:py-24 border-t border-white/5 bg-white/[0.015]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Specifications</p>
              <h2 className="text-3xl md:text-5xl font-bold">Compare NEXORA Robots</h2>
            </div>

            {/* Comparison table */}
            <div className="overflow-x-auto rounded-2xl border border-white/10 mb-14">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/90">Category</TableHead>
                    <TableHead className="text-white/90">NEXORA MINI</TableHead>
                    <TableHead className="text-white/90">NEXORA ATLAS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    ["Primary application", "Expressive interaction, content delivery, education, events", "Mobile AI + research, computer vision, robotic-arm pilots"],
                    ["Height", "850 mm (~33.5 in)", "1,660 mm (~65 in)"],
                    ["Weight", "34 kg", "Under 35 kg"],
                    ["Display", "19-inch", "8-inch head touchscreen"],
                    ["AI computing", "Onboard interaction platform", "NVIDIA Jetson AGX Orin"],
                    ["Battery runtime", "Up to 3 hours", "~6 hours"],
                    ["Speed", "Up to 0.6 m/s", "Contact Neil AI for configuration details"],
                    ["Degrees of freedom", "6 head + 16 body", "13 whole-body"],
                    ["Voice interaction", "Yes", "Yes"],
                    ["Mobility", "Bipedal expressive humanoid", "Mobile humanoid chassis"],
                    ["Robotic arm", "Not listed", "Yes — up to 3 kg payload"],
                    ["Navigation", "Manual / programmed", "LiDAR + depth camera assisted"],
                    ["Camera & sensing", "Microphone array + supported perception", "Head + wrist depth cameras, LiDAR, anti-drop sensing"],
                    ["Connectivity", "4G, Bluetooth, Wi-Fi", "Wi-Fi, Bluetooth"],
                    ["Quick-swap battery", "Yes", "Contact Neil AI for configuration details"],
                    ["Automatic charging", "Not listed", "Supported"],
                    ["Group control", "Yes (Standard)", "Contact Neil AI for configuration details"],
                    ["Content delivery", "Yes — 19-inch display", "Yes — 8-inch touchscreen"],
                    ["App management", "Yes (Standard)", "Contact Neil AI for configuration details"],
                    ["Developer support", "Secondary dev (EDU)", "Full SDK + community"],
                    ["Expansion ports", "Accessory expansion port", "USB + high-power external DC output"],
                    ["Color options", "8 official colors", "Contact Neil AI for configuration details"],
                    ["Best-fit environments", "Homes, schools, events, hospitality, retail", "Research, universities, pilots, innovation centers"],
                  ].map(([cat, a, b]) => (
                    <TableRow key={cat} className="border-white/5">
                      <TableCell className="font-medium text-white">{cat}</TableCell>
                      <TableCell className="text-slate-300">{a}</TableCell>
                      <TableCell className="text-slate-300">{b}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* ATLAS detailed spec groups */}
            <h3 className="text-2xl font-bold mb-4">NEXORA ATLAS — Detailed Specifications</h3>
            <Accordion type="multiple" className="rounded-2xl border border-white/10 divide-y divide-white/10">
              {ATLAS_SPEC_GROUPS.map((grp) => (
                <AccordionItem key={grp.group} value={grp.group} className="border-none">
                  <AccordionTrigger className="px-5 hover:no-underline text-left text-white">
                    {grp.group}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5">
                    <dl className="grid sm:grid-cols-2 gap-3">
                      {grp.rows.map((r) => (
                        <div key={r.label} className="rounded-xl bg-white/[0.03] p-3">
                          <dt className="text-xs uppercase tracking-wider text-white/50">{r.label}</dt>
                          <dd className="text-sm text-white mt-1">{r.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* VIDEOS */}
        <section id="videos" className="scroll-mt-32 py-16 md:py-24 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center max-w-2xl mx-auto mb-8">
              <p className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Media</p>
              <h2 className="text-3xl md:text-5xl font-bold">Watch NEXORA in Action</h2>
            </div>
            <Tabs value={videoFilter} onValueChange={(v) => setVideoFilter(v as typeof videoFilter)} className="w-full">
              <TabsList className="mx-auto flex w-fit bg-white/5 border border-white/10 rounded-full p-1">
                <TabsTrigger value="all" className="rounded-full">All</TabsTrigger>
                <TabsTrigger value="mini" className="rounded-full">NEXORA MINI</TabsTrigger>
                <TabsTrigger value="atlas" className="rounded-full">NEXORA ATLAS</TabsTrigger>
              </TabsList>
              <TabsContent value={videoFilter} className="mt-8">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredVideos.map((v) => (
                    <Card key={v.title} className="bg-white/[0.03] border-white/10 rounded-2xl overflow-hidden">
                      <MediaSlot label="Video Coming Soon" />
                      <div className="p-4">
                        <p className="text-[10px] uppercase tracking-widest text-primary">
                          {v.product === "mini" ? "NEXORA MINI" : "NEXORA ATLAS"}
                        </p>
                        <h3 className="mt-1 font-semibold text-white">{v.title}</h3>
                        <p className="mt-1 text-xs text-slate-300 leading-relaxed">{v.desc}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        {/* APPLICATIONS — NEXORA ATLAS Real-World Applications */}
        <section id="applications" className="scroll-mt-32 py-20 md:py-28 border-t border-white/5 bg-white/[0.015]">
          <div className="max-w-[1360px] mx-auto px-5 md:px-8">
            <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
              <p className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Real-World Applications</p>
              <h2 className="text-3xl md:text-5xl font-bold leading-tight">Designed to Support Life, Work, and Innovation</h2>
              <p className="mt-4 text-slate-300 text-base md:text-lg leading-relaxed">
                NEXORA ATLAS can be configured for approved applications across home support, senior engagement, caregiving workflows, research, education, and business innovation. Final capabilities depend on the selected hardware, software, integrations, environment, and deployment plan.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              {[
                {
                  id: "caregiving",
                  eyebrow: "Caregiving Support",
                  title: "Support everyday routines while keeping people connected to human care.",
                  image: ATLAS_CAREGIVING,
                  alt: "NEXORA ATLAS positioned beside a baby crib in a home caregiving demonstration.",
                  objectPosition: "center",
                  description:
                    "NEXORA ATLAS can be configured to support approved caregiving workflows by carrying lightweight items, displaying reminders, enabling communication, and assisting with routine engagement in controlled environments.",
                  benefitsLabel: "Customer Benefits",
                  benefits: [
                    "Helps reduce repetitive nonclinical errands.",
                    "Gives caregivers more time for direct human interaction.",
                    "Supports reminders for meals, appointments, hydration, and routines.",
                    "Can provide visual or voice-based instructions.",
                    "Helps families and staff maintain consistent schedules.",
                    "May support approved remote communication tools.",
                    "Creates an interactive presence within the care environment.",
                    "Can be adapted for personalized routines and content.",
                  ],
                  tagsLabel: "Best-Fit Environments",
                  tags: ["Private residences", "Assisted-living communities", "Adult day programs", "Rehabilitation environments", "Family-care settings", "Pilot programs"],
                  cta: "Explore Caregiving Applications",
                  disclosure:
                    "NEXORA ATLAS is not a medical device, emergency-response system, or replacement for a licensed caregiver. Appropriate supervision and human oversight are required.",
                },
                {
                  id: "elderly",
                  eyebrow: "Senior Support & Elderly Care",
                  title: "Promote independence, engagement, and peace of mind.",
                  image: ATLAS_ELDERLY_CARE,
                  alt: "NEXORA ATLAS handing a bottled drink to an older adult during a senior-support demonstration.",
                  objectPosition: "center",
                  description:
                    "NEXORA ATLAS can support older adults through approved reminder workflows, communication assistance, lightweight item delivery, interactive content, and daily engagement.",
                  benefitsLabel: "Customer Benefits",
                  benefits: [
                    "Helps seniors stay connected to family and caregivers.",
                    "Supports medication, hydration, meal, and appointment reminders.",
                    "May deliver lightweight approved items.",
                    "Reduces unnecessary walking for selected routine tasks.",
                    "Provides visual instructions and personalized content.",
                    "Supports social engagement through voice and multimedia.",
                    "Assists staff with repetitive nonclinical activities.",
                    "Helps establish more consistent daily routines.",
                  ],
                  tagsLabel: "Benefits for Senior-Living Organizations",
                  tags: [
                    "Improved resident experience",
                    "Additional support for staff",
                    "Innovation differentiation",
                    "Reduced repetitive internal-delivery activity",
                    "Enhanced family and resident engagement",
                    "Platform for future approved integrations",
                  ],
                  cta: "Explore Senior-Support Solutions",
                  disclosure:
                    "NEXORA ATLAS does not diagnose, treat, monitor, or respond to medical emergencies and is not a replacement for clinical care, required supervision, or emergency services.",
                },
                {
                  id: "home-services",
                  eyebrow: "Home Services",
                  title: "Bring intelligent assistance into everyday household routines.",
                  image: ATLAS_HOME_SERVICES,
                  alt: "NEXORA ATLAS using its robotic arm near an open refrigerator during a home-service demonstration.",
                  objectPosition: "center",
                  description:
                    "NEXORA ATLAS is designed for emerging home-service applications involving mobility, interaction, lightweight object handling, reminders, communication, and customized automation pilots.",
                  benefitsLabel: "Customer Benefits",
                  benefits: [
                    "Supports routine item-delivery demonstrations.",
                    "May retrieve or carry lightweight approved objects.",
                    "Provides household reminders and personalized information.",
                    "Supports family scheduling and communication.",
                    "May connect with approved software and smart-environment integrations.",
                    "Helps reduce repetitive movement for selected household activities.",
                    "Provides an interactive interface for instructions and assistance.",
                    "Supports education, entertainment, and family engagement.",
                  ],
                  tagsLabel: "Potential Applications",
                  tags: [
                    "Bringing bottled water or approved items",
                    "Displaying schedules",
                    "Delivering reminders",
                    "Supporting video communication",
                    "Presenting instructions or recipes",
                    "Interactive learning",
                    "Home demonstrations",
                    "Smart-environment pilots",
                  ],
                  cta: "Explore Home Applications",
                  disclosure:
                    "Home-service functions depend on the product configuration, environment, object type, software, sensors, connectivity, and supervised testing. Do not present NEXORA ATLAS as capable of unrestricted household work.",
                },
                {
                  id: "rd",
                  eyebrow: "Research & Development",
                  title: "Accelerate robotics, AI, and human-machine innovation.",
                  image: ATLAS_RD,
                  alt: "NEXORA ATLAS inside a laboratory environment for robotics and artificial-intelligence research.",
                  objectPosition: "center",
                  description:
                    "NEXORA ATLAS gives universities, laboratories, technology companies, and innovation teams a mobile humanoid platform for approved research, development, education, and experimentation.",
                  benefitsLabel: "Customer Benefits",
                  benefits: [
                    "Reduces the need to build a robotic platform from the ground up.",
                    "Provides an integrated base for AI and robotics experimentation.",
                    "Supports computer-vision projects.",
                    "Supports navigation and mapping research.",
                    "Enables human-robot interaction studies.",
                    "Supports robotic-arm and object-handling experiments.",
                    "Provides opportunities for voice and multimodal development.",
                    "Gives students practical experience with real robotics hardware.",
                    "Supports proof-of-concept and pilot development.",
                    "Creates a platform for custom software integrations.",
                  ],
                  tagsLabel: "Best-Fit Organizations",
                  tags: [
                    "Universities",
                    "Engineering schools",
                    "AI laboratories",
                    "Robotics laboratories",
                    "Corporate innovation centers",
                    "Technology startups",
                    "Research institutions",
                    "Government research programs",
                    "STEM organizations",
                    "Developer teams",
                  ],
                  cta: "Explore Research Opportunities",
                  feature: {
                    title: "Powered by NVIDIA Jetson AGX Orin",
                    copy: "The onboard NVIDIA Jetson AGX Orin platform provides advanced edge AI computing for robotics workloads including computer vision, AI inference, sensor fusion, autonomous-navigation development, voice and multimodal AI, real-time processing, and custom robotics applications.",
                  },
                }, 
              ].map((card) => {
                const visibleBenefits = card.benefits.slice(0, 3);
                const remainingBenefits = card.benefits.slice(3);
                return (
                  <Card
                    key={card.id}
                    className="group bg-white/[0.03] border-white/10 rounded-3xl overflow-hidden flex flex-col hover:border-primary/40 transition-colors shadow-[0_10px_40px_-20px_rgba(0,163,255,0.25)]"
                  >
                    <div className="p-4 md:p-5">
                      <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black border border-white/10">
                        <img
                          src={card.image}
                          alt={card.alt}
                          loading="lazy"
                          className="w-full h-full object-cover"
                          style={{ objectPosition: card.objectPosition }}
                        />
                      </div>
                    </div>

                    <div className="px-6 md:px-8 pb-8 pt-2 flex flex-col gap-5 flex-1">
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.25em] text-primary mb-2">{card.eyebrow}</p>
                        <h3 className="text-xl md:text-2xl font-bold leading-snug">{card.title}</h3>
                        <p className="mt-3 text-sm md:text-[15px] text-slate-300 leading-relaxed">{card.description}</p>
                      </div>

                      <div>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-[#D6A84F] mb-3 font-semibold">{card.benefitsLabel}</p>
                        <ul className="space-y-2">
                          {visibleBenefits.map((b) => (
                            <li key={b} className="flex gap-2 text-sm text-slate-200">
                              <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" aria-hidden />
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>

                        {remainingBenefits.length > 0 && (
                          <Accordion type="single" collapsible className="mt-2">
                            <AccordionItem value="more" className="border-none">
                              <AccordionTrigger className="py-2 text-sm text-primary hover:text-primary hover:no-underline">
                                View all benefits
                              </AccordionTrigger>
                              <AccordionContent>
                                <ul className="space-y-2 pt-1">
                                  {remainingBenefits.map((b) => (
                                    <li key={b} className="flex gap-2 text-sm text-slate-200">
                                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" aria-hidden />
                                      <span>{b}</span>
                                    </li>
                                  ))}
                                </ul>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        )}
                      </div>

                      {card.feature && (
                        <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-[#D6A84F]/5 p-5">
                          <div className="flex items-center gap-2 mb-2">
                            <Cpu className="w-5 h-5 text-primary" aria-hidden />
                            <p className="text-sm font-bold text-white">{card.feature.title}</p>
                          </div>
                          <p className="text-xs md:text-sm text-slate-300 leading-relaxed">{card.feature.copy}</p>
                        </div>
                      )}

                      <div>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-3 font-semibold">{card.tagsLabel}</p>
                        <div className="flex flex-wrap gap-2">
                          {card.tags.map((t) => (
                            <Badge key={t} variant="outline" className="border-white/15 bg-white/[0.03] text-slate-200 font-normal text-xs">
                              {t}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="mt-auto flex flex-col gap-3 pt-2">
                        <Button asChild size="sm" className="w-full sm:w-auto self-start">
                          <a href="#request-demo">
                            {card.cta}
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </a>
                        </Button>
                        {card.disclosure && (
                          <p className="text-[11px] text-slate-400 leading-relaxed flex gap-1.5">
                            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-500" aria-hidden />
                            <span>{card.disclosure}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Final CTA */}
            <div className="mt-16 md:mt-20 rounded-3xl border border-white/10 bg-gradient-to-br from-primary/10 via-white/[0.02] to-[#D6A84F]/10 p-8 md:p-12 text-center">
              <h3 className="text-2xl md:text-4xl font-bold leading-tight max-w-3xl mx-auto">
                Explore What NEXORA ATLAS Could Do for Your Environment
              </h3>
              <p className="mt-4 text-slate-300 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                Every organization and home environment is different. Neil AI evaluates your needs, physical space, workflows, users, technical requirements, and goals before recommending a NEXORA configuration or pilot.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
                  <a href="#request-demo">Request a NEXORA Assessment <ArrowRight className="w-4 h-4 ml-1" /></a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/20 bg-white/[0.03] hover:bg-white/[0.08]">
                  <a href="#request-demo">Schedule a Demonstration</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* DOWNLOADS */}
        <section className="py-16 md:py-20 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Product Resources</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "NEXORA MINI Brochure",
                "NEXORA MINI Specification Sheet",
                "NEXORA ATLAS Brochure",
                "NEXORA ATLAS Specification Sheet",
                "Product Media Kit",
                "Deployment & Training Guide",
              ].map((r) => (
                <Card key={r} className="bg-white/[0.03] border-white/10 rounded-2xl p-5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Download className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium">{r}</span>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-white/40">Coming soon</span>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 border-t border-white/5 bg-white/[0.015]">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-[0.25em] text-primary mb-3">FAQ</p>
              <h2 className="text-3xl md:text-5xl font-bold">Answers About NEXORA</h2>
            </div>
            <Accordion type="single" collapsible className="rounded-2xl border border-white/10 divide-y divide-white/10">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-none">
                  <AccordionTrigger className="px-5 text-left text-white hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 text-slate-300 text-sm leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* REQUEST DEMO */}
        <section id="request-demo" className="scroll-mt-32 py-16 md:py-24 border-t border-white/5">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Experience NEXORA</p>
              <h2 className="text-3xl md:text-5xl font-bold">Request a Demonstration</h2>
              <p className="mt-3 text-slate-300 max-w-2xl mx-auto">
                Tell us how you are considering using intelligent robotics, and a Neil AI specialist will help identify the right product, configuration, and next step.
              </p>
            </div>

            <Card className="bg-[#0A1828]/60 border-primary/20 p-6 md:p-8 rounded-3xl">
              {done ? (
                <div className="text-center py-10">
                  <CheckCircle2 className="w-14 h-14 text-[#00F5A0] mx-auto mb-4" />
                  <h3 className="text-2xl font-bold mb-2">Request received</h3>
                  <p className="text-slate-300">A NEXORA specialist will reach out shortly. For urgent needs, call (336) 203-3184.</p>
                  <Button asChild className="mt-6 rounded-full" variant="outline">
                    <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                      Schedule a Consultation
                    </a>
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" noValidate>
                  <div>
                    <Label htmlFor="firstName">First name *</Label>
                    <Input id="firstName" value={form.firstName} onChange={(e) => setField("firstName", e.target.value)} required />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last name *</Label>
                    <Input id="lastName" value={form.lastName} onChange={(e) => setField("lastName", e.target.value)} required />
                  </div>
                  <div>
                    <Label htmlFor="organization">Business or organization</Label>
                    <Input id="organization" value={form.organization} onChange={(e) => setField("organization", e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="jobTitle">Job title</Label>
                    <Input id="jobTitle" value={form.jobTitle} onChange={(e) => setField("jobTitle", e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} required />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input id="phone" type="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} required />
                  </div>
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input id="city" value={form.city} onChange={(e) => setField("city", e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="state">State / Region</Label>
                    <Input id="state" value={form.state} onChange={(e) => setField("state", e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Input id="country" value={form.country} onChange={(e) => setField("country", e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="industry">Industry</Label>
                    <Input id="industry" value={form.industry} onChange={(e) => setField("industry", e.target.value)} />
                  </div>

                  <div>
                    <Label>Product interest *</Label>
                    <Select value={form.productInterest} onValueChange={(v) => setField("productInterest", v)}>
                      <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NEXORA MINI">NEXORA MINI</SelectItem>
                        <SelectItem value="NEXORA ATLAS">NEXORA ATLAS</SelectItem>
                        <SelectItem value="Both">Both</SelectItem>
                        <SelectItem value="Not Sure">Not Sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Intended use *</Label>
                    <Select value={form.intendedUse} onValueChange={(v) => setField("intendedUse", v)}>
                      <SelectTrigger><SelectValue placeholder="Select use case" /></SelectTrigger>
                      <SelectContent>
                        {["Home","Education","Business","Hospitality","Retail","Church","Event","Research","Development","Healthcare Innovation","Senior Engagement","Other"].map((x) => (
                          <SelectItem key={x} value={x}>{x}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="quantity">Number of robots being considered</Label>
                    <Input id="quantity" value={form.quantity} onChange={(e) => setField("quantity", e.target.value)} placeholder="e.g. 1–5" />
                  </div>
                  <div>
                    <Label htmlFor="timeline">Desired timeline</Label>
                    <Input id="timeline" value={form.timeline} onChange={(e) => setField("timeline", e.target.value)} placeholder="e.g. Q2 2026" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="budget">Budget range</Label>
                    <Input id="budget" value={form.budget} onChange={(e) => setField("budget", e.target.value)} placeholder="Optional" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea id="message" rows={4} value={form.message} onChange={(e) => setField("message", e.target.value)} placeholder="Tell us about your goals, environment, or specific questions." />
                  </div>

                  <div className="sm:col-span-2 flex flex-col gap-2">
                    <label className="flex items-start gap-3 text-sm text-slate-300">
                      <Checkbox checked={form.emailConsent} onCheckedChange={(v) => setField("emailConsent", Boolean(v))} />
                      <span>I agree to receive NEXORA product updates, pricing, and follow-up emails from Neil AI Global Solutions.</span>
                    </label>
                    <label className="flex items-start gap-3 text-sm text-slate-300">
                      <Checkbox checked={form.smsConsent} onCheckedChange={(v) => setField("smsConsent", Boolean(v))} />
                      <span>I agree to receive SMS messages related to this request. Message and data rates may apply.</span>
                    </label>
                  </div>

                  <div className="sm:col-span-2 flex flex-wrap gap-3 pt-2">
                    <Button type="submit" size="lg" disabled={loading} className="h-14 px-6 rounded-full bg-gradient-to-r from-[#00A3FF] to-[#0057FF] text-white font-semibold">
                      {loading ? "Submitting…" : "Request My NEXORA Consultation"}
                    </Button>
                    <Button asChild type="button" variant="outline" size="lg" className="h-14 px-6 rounded-full border-white/20 bg-white/5">
                      <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                        Schedule a Consultation
                      </a>
                    </Button>
                  </div>
                </form>
              )}
            </Card>

            {/* Disclosure */}
            <p className="mt-8 text-[11px] text-white/45 leading-relaxed">
              Product features, specifications, battery performance, software, integrations, navigation, supported tasks, accessories, colors, and availability may vary by model, configuration, software version, operating environment, production schedule, and third-party services. Videos and images may show controlled demonstrations, development scenarios, proposed applications, or functions requiring additional configuration. NEXORA products are not substitutes for required human supervision, licensed medical professionals, caregivers, teachers, parents, emergency services, or safety personnel. Neil AI will confirm the final configuration and supported capabilities during the assessment and proposal process.
            </p>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 md:py-28 border-t border-white/5 relative overflow-hidden">
          <div aria-hidden className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,163,255,0.18),transparent_60%)]" />
          <div className="relative max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
              Bring Intelligent Robotics
              <span className="block bg-gradient-to-r from-[#00A3FF] to-[#D6A84F] bg-clip-text text-transparent">Into Your World.</span>
            </h2>
            <p className="mt-4 text-slate-300">
              Whether you are exploring robotics for education, customer engagement, business innovation, events, research, or future-ready environments, Neil AI can help you evaluate the right NEXORA solution.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Button
                size="lg"
                className="h-14 px-6 rounded-full bg-gradient-to-r from-[#00A3FF] to-[#0057FF] text-white font-semibold"
                onClick={() => document.getElementById("request-demo")?.scrollIntoView({ behavior: "smooth" })}
              >
                Request a Demonstration
              </Button>
              <Button asChild size="lg" variant="outline" className="h-14 px-6 rounded-full border-white/20 bg-white/5">
                <a href="tel:+13369624539"><Phone className="w-4 h-4 mr-2" />Talk to Neil AI</a>
              </Button>
            </div>
            <p className="mt-8 text-xs uppercase tracking-[0.3em] text-white/60">Neil AI Global Solutions LLC</p>
            <p className="mt-1 text-sm text-white/70 italic">Automate. Elevate. Dominate.™</p>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Nexora;
